import { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MyOrganizationProvider, useMyOrganization } from '@/hooks/useMyOrganization'
import { DestinationAccessProvider, useDestinationAccessReader } from '@/hooks/useDestinationAccess'
import { ErrorState } from '@/components/ui/error-state'
import { RouteSkeleton } from '@/components/ui/loader'
import { RouteSkeletonScope } from '@/components/ui/route-skeleton-scope'
import { useMatch } from 'react-router-dom'
import { AnimatedOutlet } from '@/components/ui/animated-outlet'
import { Sidebar } from '@/components/organisms/sidebar'
import { Topbar } from '@/components/organisms/topbar'
import { BrandSplash } from '@/components/brand/brand-splash'
import { BottomNav } from '@/components/organisms/bottom-nav'
import { PortalContainerProvider } from '@/components/ui/portal-container'
import { setBrandShellNode } from '@/components/ui/brand-shell'
import { PageTrailProvider } from '@/providers/PageTrailProvider'
import { UnreadProvider } from '@/providers/UnreadProvider'
import { useAuthStore } from '@/stores/auth'
import { useBrandStore } from '@/stores/brand'
import { fetchBrandSettingsForOwner } from '@/hooks/useBrandSettings'
import { applyBrandVars, APP_FONT_STACKS } from '@/lib/brand-colors'
import { statedTenantMode } from '@/lib/event-theme'
import { useThemeStore } from '@/stores/theme'
import { STORAGE_KEYS } from '@/lib/constants'
import { getCached, setCached } from '@/lib/query-cache'
import type { Database } from '@/lib/types/database'

type BrandSettings = Database['public']['Tables']['brand_settings']['Row']

const BRAND_TTL_MS = 5 * 60 * 1000
const brandCacheKey = (userId: string) => `brand_settings:${userId}`
const brandSnapshotKey = (userId: string) => `er:brand:${userId}`

/**
 * A white-label tenant's default light/dark side, applied like its colours.
 *
 * `brand_settings.default_theme` was dead data everywhere except the anonymous
 * `/event/public/:token` route: nothing in the signed-in app read it, so
 * Nortura PROFF — `default_theme: 'dark'` since 03.06.2026 — rendered its own
 * admins the app in whatever side their OS happened to say, and the SAME event
 * came up dark on the share link and light on `/event/:id`.
 *
 * It goes through the theme store's ORG DEFAULT tier, never a classList write:
 * the store is the single owner of the html class, and the tier is what makes
 * this a DEFAULT rather than an imposition — a person who has pressed the
 * theme control still wins, and an event's own theme still outranks the
 * account-wide one.
 *
 * The device key mirrors it for the pre-paint script in `index.html`, so a cold
 * load paints the tenant's side before first paint instead of flashing light —
 * the same job `brandSnapshotKey` does for the tenant's colours.
 */
function applyTenantDefaultMode(settings: BrandSettings | null) {
  const mode = statedTenantMode(settings?.default_theme)
  useThemeStore.getState().setOrgDefaultMode(mode)
  try {
    if (mode) localStorage.setItem(STORAGE_KEYS.ORG_DEFAULT_THEME, mode)
    else localStorage.removeItem(STORAGE_KEYS.ORG_DEFAULT_THEME)
  } catch {
    /* storage unavailable — the store still holds it for this session */
  }
}

/**
 * The app chrome states its own typography, always.
 *
 * David, 06.08.2026: «are you using the Nortura PROFF font for the entire app?
 * should only be Nortura PROFFs white label font.» He was right, and the leak
 * had a specific source: `useEventThemeOnDocument` writes the event's brand
 * vars — fonts included — to `document.documentElement`, ABOVE this shell. Its
 * own comment says that is deliberate, «including the app sidebar/topbar». So
 * opening a Nortura event repainted the whole app in Bree, for any viewer.
 *
 * A tenant's font belongs to that tenant's surface. The sidebar, the topbar and
 * the rest of the chrome belong to whoever is signed in — which is why the fix
 * is here, at the boundary, and not in the hook: pinning the values the chrome
 * owns cannot be defeated by the next thing that writes to the document, and
 * there will be a next thing.
 *
 * Colours deliberately still follow the event. «A branded event looks branded»
 * is the behaviour that comment describes and it is not what was wrong; a
 * branded event repainting every OTHER page's typography was.
 */
function pinAppTypography(el: HTMLElement, stack?: string | null) {
  for (const [k, v] of Object.entries(APP_FONT_STACKS)) el.style.setProperty(k, v)
  // And the PROPERTY, not only the variable.
  //
  // Measured: declaring `--font-sans` on the shell changed nothing, because
  // `body` resolves the variable once and every descendant then inherits the
  // COMPUTED font-family. Re-declaring the variable lower down is invisible
  // unless something re-resolves it. Setting `font-family` here makes the shell
  // the origin of inheritance for everything inside it, which is what «the app
  // chrome owns its own typography» actually requires.
  el.style.fontFamily = stack || APP_FONT_STACKS['--font-sans']
}

function applyBrandSettings(el: HTMLElement, settings: BrandSettings | null) {
  // ALWAYS pin first, then let a real brand stack overwrite it.
  //
  // The first version of this pinned only when `font_family` was null, and it
  // did not work: TOLLGAARDEN's row says «Inter», which is the app's own face,
  // so `brandFontStack` returns nothing and NOTHING was declared on the shell —
  // which is exactly the inheriting state the fix exists to prevent. Measured:
  // the sidebar still went Bree. Pinning unconditionally has no such gap,
  // because there is no condition.
  // The shell's TEXT face stays the app's; the brand face reaches headings through
  // --font-display (applyBrandVars). 19.10.2026: Bree on every 13px label was the brand at its worst.
  pinAppTypography(el)
  applyBrandVars(el, {
    primaryHex: settings?.primary_color ?? null,
    secondaryHex: settings?.secondary_color,
    tertiaryHex: settings?.tertiary_color,
    neutralHex: settings?.neutral_color,
    fontFamily: settings?.font_family,
  })
}

/** Persisted brand snapshot — survives hard reloads so a white-label tenant
 * never flashes the default palette before the async fetch resolves. */
function readBrandSnapshot(userId: string): BrandSettings | null {
  try {
    const raw = localStorage.getItem(brandSnapshotKey(userId))
    return raw ? (JSON.parse(raw) as BrandSettings) : null
  } catch {
    return null
  }
}
function writeBrandSnapshot(userId: string, settings: BrandSettings | null) {
  try {
    if (settings) localStorage.setItem(brandSnapshotKey(userId), JSON.stringify(settings))
    else localStorage.removeItem(brandSnapshotKey(userId))
  } catch {
    /* storage unavailable — snapshot is only an optimization */
  }
}

export function AppLayout() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const organization = useMyOrganization()
  const destinationAccess = useDestinationAccessReader(
    organization.activeOrgId,
    organization.active?.org.offers_enabled !== false,
  )
  const brandVersion = useBrandStore((s) => s.version)
  const eventThemeActive = useBrandStore((s) => s.eventThemeActive)
  // Node kept in state (not a ref) so the PortalContainerProvider re-renders
  // once the shell mounts. Radix portals (Modal, DropdownMenu, …) then mount
  // INTO this brand-scoped node so tenant white-label CSS vars reach them.
  const [shellNode, setShellNode] = useState<HTMLDivElement | null>(null)

  // Floating chrome (InstallPrompt, UpdateBanner) is mounted in `App.tsx`,
  // ABOVE the provider above, so it cannot read the context — and it must still
  // inherit the tenant palette this shell carries. Publishing the node the shell
  // already owns is the whole mechanism; see `portal-container.tsx` for the
  // measurement that made it necessary and for why a DOM lookup is not safe here.
  useLayoutEffect(() => {
    setBrandShellNode(shellNode)
    return () => setBrandShellNode(null)
  }, [shellNode])

  // Inject brand color CSS vars scoped to the app shell only (not landing/auth).
  // useLayoutEffect + snapshot/cache: apply a known brand synchronously before
  // paint, then reconcile with the network. Re-runs on login, shell mount, or a
  // brand save (brandVersion bump).
  useLayoutEffect(() => {
    if (!shellNode) return
    if (!user?.id) {
      applyBrandVars(shellNode, null)
      pinAppTypography(shellNode)
      // Signed out: the tenant default goes with the tenant, or the next
      // person on this browser inherits somebody else's brand.
      applyTenantDefaultMode(null)
      return
    }
    // A mounted per-event theme owns the document (useEventThemeOnDocument).
    // Clear the shell's org vars so the event's tokens flow through — the
    // shell's INLINE values would beat the event's document-level ones for the
    // whole visible page, splitting `/event/:id` from the public route. The
    // chrome still pins its own typography. Restored when the flag drops
    // (this effect re-runs on eventThemeActive).
    if (eventThemeActive) {
      applyBrandVars(shellNode, null)
      pinAppTypography(shellNode)
      // The tenant default is deliberately NOT cleared here. The event lock
      // already outranks it in the store, so clearing would be a second
      // mechanism doing the same job — and the wrong one: `unlockMode` needs
      // the tenant default still standing to fall back to when the viewer
      // leaves the event.
      return
    }
    if (organization.loading || organization.error) return
    const uid = user.id
    // Pre-paint apply from the persisted snapshot or the warm in-memory cache.
    const known = readBrandSnapshot(uid) ?? getCached<BrandSettings | null>(brandCacheKey(uid), BRAND_TTL_MS)
    if (known) {
      applyBrandSettings(shellNode, known)
      applyTenantDefaultMode(known)
    }

    let alive = true
    fetchBrandSettingsForOwner(uid)
      .then((settings) => {
        if (!alive) return // stale response after unmount / user change
        applyBrandSettings(shellNode, settings)
        applyTenantDefaultMode(settings)
        setCached(brandCacheKey(uid), settings)
        writeBrandSnapshot(uid, settings)
      })
      .catch(() => {
        /* network/permission error — keep the snapshot/default already applied */
      })
    // NB: no reset-to-null here — a brandVersion bump just re-applies (overwrites)
    // without flashing the default; null is only applied when the user is gone.
    return () => {
      alive = false
    }
  }, [shellNode, user?.id, brandVersion, eventThemeActive, organization.loading, organization.error, organization.activeOrgId])

  // Messages page needs full-bleed fixed-height layout (no padding, no outer scroll)
  const matchMessages = useMatch('/messages')
  const matchMessagesId = useMatch('/messages/:eventId')
  const isMessages = !!(matchMessages || matchMessagesId)

  /**
   * Inside an event, the bottom bar belongs to the EVENT.
   *
   * Measured before this: `/event/:id` on a phone drew TWO fixed bars — the
   * event's own tabs at 65px and the app's nav at 65px — 130px of an 812px
   * viewport presenting ten destinations with no hierarchy between them. The
   * way out is not deleted, it moves into the event bar's «Mer» sheet, which
   * is where a person looks for «everything else» anyway.
   *
   * `--app-nav-inset` follows automatically: it is set beside the padding that
   * reserves the same space, so suppressing one suppresses both, and
   * `--bottom-chrome-inset` keeps floating chrome correct without knowing why.
   */
  const insideEvent = !!useMatch('/event/:id')
  /**
   * A focused editor owns the bottom edge too (04.10.2026 re-score). On a phone the
   * editor's «Lagre endringer» StickyCtaBar sat on top of this nav: 135px of bottom
   * chrome under every form, five destinations nobody leaves an unsaved form for.
   * The way out is the editor's own back button, which returns to the event, whose
   * bar has «Mer». Same mechanism as `insideEvent`, so `--app-nav-inset` follows.
   */
  const inEventEditor = !!useMatch('/event/:id/edit')
  const inCreateEvent = !!useMatch('/create-event')
  const inFocusedEditor = inEventEditor || inCreateEvent
  const hideAppNav = insideEvent || inFocusedEditor

  return (
    // ref=setShellNode: brand CSS vars are injected here + portals mount here.
    // 100svh (small viewport height) — never includes browser chrome that
    // dynamically shows/hides on iOS, so the layout doesn't jump when the URL
    // bar collapses. Combined with viewport's interactive-widget=resizes-content,
    // the keyboard properly resizes the layout instead of obscuring it.
    <div
      ref={setShellNode}
      className="flex h-[100svh] overflow-hidden bg-[var(--surface-page)]"
      style={{
        // iOS PWA + viewport-fit=cover: push the whole shell below the notch.
        // This USED to live on the Topbar, which was safe only while the bar
        // rendered on every route. It no longer does (18d removes it from the
        // fifteen flat routes at lg+), so an inset owned by the bar would
        // vanish with it and content would slide under the notch on an iPad
        // standalone PWA. Owning it here makes it independent of the bar.
        // box-sizing: border-box keeps the shell at exactly 100svh.
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      {/* Oppstartsbildet ligger her, ikke i roten, så det bare møter folk som
          åpner selve appen. En førstegangsbesøkende på en delt lenke treffer
          PublicLayout og slipper det. Det blokkerer ingenting: skallet under
          tegnes som vanlig mens lokket tones bort. */}
      <BrandSplash />
      <MyOrganizationProvider value={organization}>
      <DestinationAccessProvider value={destinationAccess}>
      <PortalContainerProvider value={shellNode}>
        {/* Uleste meldinger og varsler, ett sted for hele skallet. Sidemenyen,
            bunnmenyen og `/messages` leste dette hver for seg før — tre kopier
            som ikke kunne bli enige om når et merke skulle bort. Se
            `hooks/useUnread.ts`. */}
        <UnreadProvider>
          {/* Desktop sidebar */}
          <Sidebar />

          {/* Inner column inherits its height from the flex-row parent's stretch.
              BottomNav is position: fixed on mobile, so we add padding-bottom on
              mobile to reserve its space (lg:pb-0 since BottomNav hides on lg+). */}
          {/* `--app-nav-inset` is this layout telling everything inside it how
              much bottom edge is already spoken for (TA2). It is set here, beside
              the padding that reserves the same space, because those two numbers
              must never disagree — and reset at `lg` for the same reason `pb-0`
              is: `BottomNav` is `lg:hidden`. */}
          <div
            className={
              hideAppNav
                ? 'flex min-w-0 flex-1 flex-col overflow-hidden app-nav-inset-none'
                : 'flex min-w-0 flex-1 flex-col overflow-hidden pb-[var(--bottom-nav-measured,var(--bottom-nav-height))] lg:pb-0 app-nav-inset-measured lg:app-nav-inset-none'
            }
          >
            {/* The bar renders conditionally now (see topbar.tsx), and the depth
                trail it shows is published by the page — a UUID in the path
                cannot name the record it points at. */}
            <PageTrailProvider>
              <Topbar />

              <RouteSkeletonScope.Provider value="page">
              <main id="main-content" className="min-h-0 flex-1 min-w-0 flex flex-col overflow-hidden">
                {organization.error ? <ErrorState description={t('org.loadError')} onRetry={organization.refetch} /> : organization.loading ? (
                  // Same padding and shape as the page that follows (24.10.2026: this was a full-bleed
                  // generic skeleton, the first of several shapes in David's recording).
                  <div className={isMessages ? 'flex min-h-0 flex-1 min-w-0 flex-col' : 'page-scroller'}><RouteSkeleton /></div>
                ) : <AnimatedOutlet frame={isMessages ? 'fill' : 'page'} />}
              </main>
              </RouteSkeletonScope.Provider>
            </PageTrailProvider>

            {!hideAppNav && <BottomNav />}
          </div>
        </UnreadProvider>
      </PortalContainerProvider>
      </DestinationAccessProvider>
      </MyOrganizationProvider>
    </div>
  )
}
